import { Stage, Layer, Group, Line } from 'react-konva';
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import entityComposition from '../classes/entityComposition';
import pageProperties from './pageProperties';
import annTools from './annotations/commonAnnotationTools';
import Highlight from './annotations/highlight';
import FreeText from './annotations/freeText';
import Note from './annotations/note';
import Info from './annotations/info';
import Ink from './annotations/ink';
const lineThickness = 8;
const baseInkThickness = 5;

/**
 * Extract baseType and formTypeIdentifier from selectableType composite key
 * Handles defensive programming for edge cases
 * @param {string|null} selectableType - Composite key like 'line__lineAnnotation' or null
 * @returns {{baseType: string|null, formTypeIdentifier: string|null}} Object with extracted values
 */
const parseSelectableType = selectableType => {
  if (!selectableType) {
    return {
      baseType: null,
      formTypeIdentifier: null
    };
  }
  const parts = selectableType.split('__');
  return {
    baseType: parts.length > 0 ? parts[0] : null,
    formTypeIdentifier: parts.length > 1 ? parts[1] : null
  };
};

// Helper to build composite identifier
const getAnnotationIdentifier = (renderType, formType) => {
  const render = renderType || '';
  const form = formType && formType.identifier ? "__".concat(formType.identifier) : '';
  return "".concat(render).concat(form);
};

/**
 *  Reviews the clicked entity and the active annotation id to see which was clicked
 */
export const parseWhatWasClicked = function parseWhatWasClicked(selectableType) {
  var _clickedAnnotation$in;
  let clickedAnnotation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const formType = clickedAnnotation.formType;
  const _parseSelectableType = parseSelectableType(selectableType),
    renderType = _parseSelectableType.baseType;
  let annotationProp = "".concat(renderType, "Annotation") || null;

  // special case for issue, which uses { issue: {} } rather than { issueAnnotation: {} }
  if (clickedAnnotation.issue && selectableType === 'issue') {
    annotationProp = 'issue';
  }

  // special case for line, which is an ink annotation with unsmoothed=1
  if ((_clickedAnnotation$in = clickedAnnotation.inkAnnotation) !== null && _clickedAnnotation$in !== void 0 && _clickedAnnotation$in.unsmoothed && renderType === 'line') {
    annotationProp = 'inkAnnotation';
  }

  // Dynamically check the correct annotation property based on renderType and formType
  const compositeKey = getAnnotationIdentifier(renderType, formType);
  if (annotationProp && clickedAnnotation[annotationProp] && selectableType === compositeKey) {
    return {
      type: renderType,
      annotation: clickedAnnotation
    };
  }
  return {
    type: 'stage'
  };
};

/**
 *  Used to generate arrays of points for ink and line annotations
 */
/* eslint-disable-next-line default-param-last */
export const generatePointsArray = function generatePointsArray() {
  let points = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let makeLine = arguments.length > 1 ? arguments[1] : undefined;
  const pts = [];
  if (points && Array.isArray(points)) {
    points.forEach(next => {
      pts.push(next.x);
      pts.push(next.y);
    });
  }
  if (pts.length === 2) {
    pts.push(pts[0]);
    pts.push(pts[1]);
  }
  // if we want a line, we only want the first and last, otherwise ink returns all points
  return makeLine ? [pts[0], pts[1], pts[pts.length - 2], pts[pts.length - 1]] : pts;
};

/**
 *  The component
 */
const PDFAnnotationsLayer = _ref => {
  let activeAnnotation = _ref.annotation.active,
    _ref$annotationSelect = _ref.annotationSelection,
    annotationContext = _ref$annotationSelect.annotationContext,
    clickedEntity = _ref$annotationSelect.clickedEntity,
    defaultColor = _ref$annotationSelect.defaultColor,
    selectableType = _ref$annotationSelect.selectableType,
    editMode = _ref$annotationSelect.editMode,
    points = _ref$annotationSelect.points,
    trackCursorPosition = _ref$annotationSelect.trackCursorPosition,
    isDrawing = _ref$annotationSelect.isDrawing,
    _ref$annotationSelect2 = _ref.annotationSelectionActions,
    batchChange = _ref$annotationSelect2.batchChange,
    trackCursor = _ref$annotationSelect2.trackCursor,
    trackReset = _ref$annotationSelect2.trackReset,
    startDrawing = _ref$annotationSelect2.startDrawing,
    setClickedEntity = _ref$annotationSelect2.setClickedEntity,
    pageNumber = _ref.pageNumber,
    onClick = _ref.onClick,
    hideTextLayer = _ref.hideTextLayer,
    showTextLayer = _ref.showTextLayer,
    annotation = _ref.annotation,
    _ref$canCreateDrawing = _ref.canCreateDrawingAnnotations,
    canCreateDrawingAnnotations = _ref$canCreateDrawing === void 0 ? null : _ref$canCreateDrawing,
    _ref$canEditFormAnnot = _ref.canEditFormAnnotations,
    canEditFormAnnotations = _ref$canEditFormAnnot === void 0 ? null : _ref$canEditFormAnnot,
    _ref$evaluateAnnotati = _ref.evaluateAnnotationHandlerColor,
    evaluateAnnotationHandlerColor = _ref$evaluateAnnotati === void 0 ? null : _ref$evaluateAnnotati,
    _ref$reportAnnotation = _ref.reportAnnotation,
    activeReportAnnotation = _ref$reportAnnotation.active,
    freeTextsByPage = _ref$reportAnnotation.freeTextsByPage,
    highlightsByPage = _ref$reportAnnotation.highlightsByPage,
    infosByPage = _ref$reportAnnotation.infosByPage,
    inksByPage = _ref$reportAnnotation.inksByPage,
    issuesByPage = _ref$reportAnnotation.issuesByPage,
    linesByPage = _ref$reportAnnotation.linesByPage,
    loading = _ref$reportAnnotation.loading;
  const stageRef = useRef(null);
  const annotationLayerRef = useRef(null);
  // Ref to store color from annotationHandler.create for temporary line
  // This does NOT alter the global defaultColor, only affects the temporary drawing
  // Using useRef instead of useState because we need immediate synchronous access
  // after await, before React re-renders
  const temporaryLineColorRef = useRef(null);
  const active = annotationContext === 'form' ? activeAnnotation : activeReportAnnotation;
  const selectedID = active && active.id ? active.id : '';
  const _pageProperties$getCa = pageProperties.getCanvasProperties(pageNumber),
    scale = _pageProperties$getCa.scale,
    pageHeight = _pageProperties$getCa.pageHeight,
    pageWidth = _pageProperties$getCa.pageWidth,
    offset = _pageProperties$getCa.offset,
    dpiScale = _pageProperties$getCa.dpiScale;
  const inkAnnotations = useMemo(() => {
    var _annotation$groupedAn;
    return annotationContext === 'form' ? (annotation === null || annotation === void 0 || (_annotation$groupedAn = annotation.groupedAnnotations) === null || _annotation$groupedAn === void 0 || (_annotation$groupedAn = _annotation$groupedAn[pageNumber]) === null || _annotation$groupedAn === void 0 ? void 0 : _annotation$groupedAn.Ink) || [] : inksByPage[pageNumber] || [];
  }, [annotationContext, annotation, pageNumber, inksByPage]);
  const lineAnnotations = useMemo(() => {
    var _annotation$groupedAn2;
    const annotations = annotationContext === 'form' ? (annotation === null || annotation === void 0 || (_annotation$groupedAn2 = annotation.groupedAnnotations) === null || _annotation$groupedAn2 === void 0 || (_annotation$groupedAn2 = _annotation$groupedAn2[pageNumber]) === null || _annotation$groupedAn2 === void 0 ? void 0 : _annotation$groupedAn2.Line) || [] : linesByPage[pageNumber] || [];
    return annotations;
  }, [annotationContext, annotation, pageNumber, linesByPage]);
  const highlightAnnotations = useMemo(() => {
    var _annotation$groupedAn3;
    return annotationContext === 'form' ? (annotation === null || annotation === void 0 || (_annotation$groupedAn3 = annotation.groupedAnnotations) === null || _annotation$groupedAn3 === void 0 || (_annotation$groupedAn3 = _annotation$groupedAn3[pageNumber]) === null || _annotation$groupedAn3 === void 0 ? void 0 : _annotation$groupedAn3.Highlight) || [] : highlightsByPage[pageNumber] || [];
  }, [annotationContext, annotation, pageNumber, highlightsByPage]);
  const freeTextAnnotations = useMemo(() => {
    var _annotation$groupedAn4;
    return annotationContext === 'form' ? (annotation === null || annotation === void 0 || (_annotation$groupedAn4 = annotation.groupedAnnotations) === null || _annotation$groupedAn4 === void 0 || (_annotation$groupedAn4 = _annotation$groupedAn4[pageNumber]) === null || _annotation$groupedAn4 === void 0 ? void 0 : _annotation$groupedAn4.FreeText) || [] : freeTextsByPage[pageNumber] || [];
  }, [annotationContext, annotation, pageNumber, freeTextsByPage]);
  const infoAnnotations = useMemo(() => {
    var _annotation$groupedAn5;
    return annotationContext === 'form' ? (annotation === null || annotation === void 0 || (_annotation$groupedAn5 = annotation.groupedAnnotations) === null || _annotation$groupedAn5 === void 0 || (_annotation$groupedAn5 = _annotation$groupedAn5[pageNumber]) === null || _annotation$groupedAn5 === void 0 ? void 0 : _annotation$groupedAn5.Info) || [] : infosByPage[pageNumber] || [];
  }, [annotationContext, annotation, pageNumber, infosByPage]);
  const noteAnnotations = useMemo(() => {
    var _annotation$groupedAn6;
    return (annotation === null || annotation === void 0 || (_annotation$groupedAn6 = annotation.groupedAnnotations) === null || _annotation$groupedAn6 === void 0 || (_annotation$groupedAn6 = _annotation$groupedAn6[pageNumber]) === null || _annotation$groupedAn6 === void 0 ? void 0 : _annotation$groupedAn6.Note) || [];
  }, [annotation, pageNumber]);
  const issueAnnotations = useMemo(() => issuesByPage[pageNumber] || [], [issuesByPage, pageNumber]);
  const validEditKnobs = useMemo(() => ['end', 'beginning', 'ul', 'ur', 'll', 'lr'], []);

  // Memoize parseSelectableType result to avoid recalculating on every dot added
  const parsedSelectableType = useMemo(() => parseSelectableType(selectableType), [selectableType]);
  useEffect(() => {
    // Extract base type from composite key (e.g., 'highlight__highlightAnnotation' -> 'highlight')
    const baseType = parsedSelectableType.baseType;
    if (baseType === 'highlight') {
      showTextLayer();
    } else {
      hideTextLayer();
    }
  }, [parsedSelectableType, hideTextLayer, showTextLayer]);

  // Reset temporary color when selectableType changes (new annotation type selected)
  useEffect(() => {
    temporaryLineColorRef.current = null;
  }, [selectableType]);

  // Clear temporary color when drawing ends (isDrawing becomes false)
  // This happens after the annotation is created and the temporary line is no longer rendered
  useEffect(() => {
    if (!isDrawing && temporaryLineColorRef.current) {
      temporaryLineColorRef.current = null;
    }
  }, [isDrawing]);

  // Create a stable key for the Stage that only changes when dimensions are significantly different
  const stageKey = useMemo(() => {
    if (!pageWidth || !pageHeight) return 'loading';
    // Round to nearest 10 to prevent micro-changes from causing re-renders
    const stableWidth = Math.round(pageWidth / 10) * 10;
    const stableHeight = Math.round(pageHeight / 10) * 10;
    return "".concat(pageNumber, "-").concat(stableWidth, "-").concat(stableHeight);
  }, [pageNumber, pageWidth, pageHeight]);

  // Check if user can edit annotations (for draggable property)
  const canEdit = canEditFormAnnotations ? canEditFormAnnotations() : true;

  // group the annotations by page and only return the ones that are on this page
  const grouped = annTools.groupPointsByPage(points);
  const lines = (grouped || []).filter(nextGroup => nextGroup.length && nextGroup[0].pageNumber === pageNumber);

  // Build a map of composite keys to annotation arrays
  const annotationsByType = useMemo(() => {
    const map = {};
    // Helper to add all annotations of a given type
    const addAnnotations = (arr, renderType) => {
      arr.forEach(ann => {
        // Use formTypeIdentifier or formType?.identifier as appropriate
        const formType = ann.formType || ann.formTypeIdentifier;
        const key = getAnnotationIdentifier(renderType, formType);
        if (!map[key]) {
          map[key] = [];
        }
        map[key].push(ann);
      });
    };
    addAnnotations(highlightAnnotations, 'highlight');
    addAnnotations(inkAnnotations, 'ink');
    addAnnotations(lineAnnotations, 'line');
    addAnnotations(freeTextAnnotations, 'freeText');
    addAnnotations(infoAnnotations, 'info');
    addAnnotations(noteAnnotations, 'note');
    // 'issue' is not keyed by composite, just by 'issue'
    map.issue = issueAnnotations;
    return map;
  }, [highlightAnnotations, inkAnnotations, lineAnnotations, freeTextAnnotations, infoAnnotations, noteAnnotations, issueAnnotations]);

  // Function to evaluate annotationHandler.create and get color for temporary line
  // Uses the callback passed from formAnnotationEditor
  // Stores color in local state (does NOT alter global defaultColor)
  const handleEvaluateAnnotationHandlerColor = useCallback(async (baseType, formTypeIdentifier) => {
    if (!evaluateAnnotationHandlerColor) {
      return;
    }
    try {
      const newColor = await evaluateAnnotationHandlerColor(baseType, formTypeIdentifier);
      if (newColor) {
        // Store in ref for immediate synchronous access (before React re-render)
        temporaryLineColorRef.current = newColor;
      } else {
        temporaryLineColorRef.current = null;
      }
    } catch (error) {
      // Silently fail - use default color if handler evaluation fails
      temporaryLineColorRef.current = null;
    }
  }, [evaluateAnnotationHandlerColor]);

  // Inlined and memoized event handlers
  const handleMouseDown = useCallback(async event => {
    const target = event.target;
    const cursorPosition = target.getStage().getPointerPosition();
    const rotation = target.getStage().rotation() || 0;
    if (editMode && target.nodeType === 'Stage') {
      if (Object.keys(active).length > 0) {
        onClick('stage');
      } else {
        // Extract base type and formTypeIdentifier from composite key
        // (e.g., 'line__lineAnnotation' -> 'line', 'lineAnnotation')
        const baseType = parsedSelectableType.baseType,
          formTypeIdentifier = parsedSelectableType.formTypeIdentifier;
        if (baseType === 'ink' || baseType === 'line') {
          // Check permission before starting to draw
          if (canCreateDrawingAnnotations && !canCreateDrawingAnnotations()) {
            return; // Exit early if user doesn't have permission
          }

          // Evaluate annotationHandler.create BEFORE starting to draw
          // This allows us to get the correct color for the temporary line
          if (formTypeIdentifier) {
            await handleEvaluateAnnotationHandlerColor(baseType, formTypeIdentifier);
          }
          trackReset();
          trackCursor(cursorPosition, rotation, pageNumber);
          startDrawing();
        } else {
          const newPDFPoint = annTools.convertPointToPDFCoords(cursorPosition, pageNumber);
          onClick('stage', null, {
            rotation,
            pageNumber,
            xLocation: newPDFPoint.x,
            yLocation: newPDFPoint.y
          });
        }
      }
    } else if (target.getParent() && target.getParent().nodeType === 'Group') {
      // Handle clicks on existing annotation elements
      const targetName = target.getAttr('name');
      const parent = target.getParent();
      const parentSelectedID = parent && parent.attrs && parent.attrs.name ? parent.attrs.name : '';
      const previouslySelectedID = active.id || null;
      if (validEditKnobs.includes(targetName)) {
        setClickedEntity({
          name: targetName,
          originalAnnotation: JSON.parse(JSON.stringify(active))
        });
      } else if (parentSelectedID && parentSelectedID !== previouslySelectedID) {
        var _annotationsByType$se;
        const foundAnnotation = (_annotationsByType$se = annotationsByType[selectableType]) === null || _annotationsByType$se === void 0 ? void 0 : _annotationsByType$se.find(next => String(next.id) === String(parentSelectedID));
        const _parseWhatWasClicked = parseWhatWasClicked(selectableType, foundAnnotation),
          foundType = _parseWhatWasClicked.type,
          clickedAnnotation = _parseWhatWasClicked.annotation;
        if (clickedAnnotation) {
          onClick(foundType, clickedAnnotation);
        } else {
          onClick(foundType);
        }
      }
    } else {
      onClick('stage');
    }
  }, [parsedSelectableType, selectableType, trackCursor, trackReset, startDrawing, setClickedEntity, annotationsByType, onClick, pageNumber, active, editMode, validEditKnobs, canCreateDrawingAnnotations, handleEvaluateAnnotationHandlerColor]);
  const handleMouseMove = useCallback(event => {
    const target = event.target;
    if (trackCursorPosition && !loading) {
      trackCursor(target.getStage().getPointerPosition(), target.getStage().rotation() || 0, pageNumber);
    }
  }, [loading, trackCursor, trackCursorPosition, pageNumber]);
  const handleMouseUp = useCallback(() => {
    if (trackCursorPosition) {
      // Extract base type to check if this is a line/ink annotation
      const baseType = parsedSelectableType.baseType;
      // For line/ink annotations, we need at least 2 points to finalize
      // A single point is just the first click, not the end of drawing
      const isLineOrInk = baseType === 'ink' || baseType === 'line';
      const hasEnoughPoints = isLineOrInk ? points.length >= 2 : points.length > 0;
      if (isDrawing && hasEnoughPoints) {
        const strokeWidth = baseType === 'line' ? lineThickness : baseInkThickness / scale;

        // For drawing annotations, pass the points and stroke width
        // The onClick handler will route this to the appropriate context
        onClick('stage', null, {
          strokeWidth,
          points
        });

        // Don't clear temporary color here - keep it until isDrawing becomes false
        // This prevents the line from flashing red before the annotation is created
      } else if (!isDrawing) {
        // Don't reset if we're just starting to draw (first click for line/ink)
        trackReset();
      }
    }
    if (Object.keys(clickedEntity || {}).length > 0) {
      setClickedEntity({});
    }
  }, [clickedEntity, setClickedEntity, onClick, isDrawing, points, scale, trackReset, trackCursorPosition, parsedSelectableType]);
  const handleDragEnd = useCallback(event => {
    const target = event.target;
    if (target && target.nodeType === 'Group') {
      // Check permission before allowing drag/move operations
      if (canEditFormAnnotations && !canEditFormAnnotations()) {
        // Reset the position if user doesn't have permission
        target.setAttr('x', 0);
        target.setAttr('y', 0);
        return; // Exit early if user doesn't have permission
      }
      const changes = [];
      const xOffset = target.getAttr('x');
      const yOffset = target.getAttr('y');
      const scaleDPI = scale / dpiScale;
      const scaledXOffset = xOffset / scaleDPI;
      const scaledYOffset = yOffset / scaleDPI;
      target.setAttr('x', 0);
      target.setAttr('y', 0);
      if (selectableType && (selectableType.startsWith('ink') || selectableType.startsWith('line'))) {
        const _ref2 = active.inkAnnotation || {},
          pathString = _ref2.pathString;
        const newPathString = annTools.translatePathString(pathString, scaledXOffset, scaledYOffset);
        changes.push({
          id: active.id,
          path: 'inkAnnotation/pathString',
          data: newPathString
        });
      }
      const rectString = active.rectString;
      const newRectString = annTools.translateRectString(rectString, scaledXOffset, scaledYOffset);
      changes.push({
        id: active.id,
        path: 'rectString',
        data: newRectString
      });
      batchChange(changes);
    }
  }, [batchChange, active, scale, dpiScale, selectableType, canEditFormAnnotations]);
  const handleEdit = useCallback(event => {
    const target = event.target;
    const _ref3 = clickedEntity || {},
      clickedName = _ref3.name,
      originalAnnotation = _ref3.originalAnnotation;
    if (originalAnnotation && validEditKnobs.includes(clickedName)) {
      const rectString = originalAnnotation.rectString,
        inkAnnotation = originalAnnotation.inkAnnotation;
      let newPathString = '';
      const newKnobPositionCanvasCoord = {
        x: target.getAttr('x'),
        y: target.getAttr('y')
      };
      const newKnobPDFCoord = annTools.convertPointToPDFCoords(newKnobPositionCanvasCoord, pageNumber);
      if (inkAnnotation && ['end', 'beginning'].includes(clickedName)) {
        newPathString = annTools.returminateLine(inkAnnotation.pathString, newKnobPDFCoord, clickedName);
      } else if (['ul', 'ur', 'll', 'lr'].includes(clickedName)) {
        newPathString = annTools.scalePathString(inkAnnotation && inkAnnotation.pathString ? inkAnnotation.pathString : '', rectString, newKnobPDFCoord, clickedName);
      }
      const newRectString = annTools.recalcRectStringFromPath(newPathString);
      const changes = [];
      if (inkAnnotation && inkAnnotation.pathString) {
        changes.push({
          id: active.id,
          path: 'inkAnnotation/pathString',
          data: newPathString
        });
      }
      changes.push({
        id: active.id,
        path: 'rectString',
        data: newRectString
      });
      batchChange(changes);
      annTools.moveTargetToProperPosition(target, clickedName, newRectString, pageNumber);
    }
  }, [clickedEntity, validEditKnobs, batchChange, active, pageNumber]);
  return /*#__PURE__*/React.createElement(Stage, {
    width: pageWidth,
    height: pageHeight,
    ref: stageRef,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    className: "t-pal-stage",
    key: stageKey,
    contextAttributes: {
      willReadFrequently: true
    }
  }, /*#__PURE__*/React.createElement(Layer, {
    ref: annotationLayerRef
  }, /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-highlight")
  }, highlightAnnotations.map(ann => /*#__PURE__*/React.createElement(Highlight, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id
  }))), /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-ink")
  }, inkAnnotations.map(ann => /*#__PURE__*/React.createElement(Ink, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id,
    handleEdit: handleEdit
  }))), /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-line")
  }, lineAnnotations.map(ann => /*#__PURE__*/React.createElement(Ink, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id,
    handleEdit: handleEdit
  }))), /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-free-text")
  }, freeTextAnnotations.map(ann => /*#__PURE__*/React.createElement(FreeText, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id,
    handleEdit: handleEdit
  }))), /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-issue")
  }, issueAnnotations.map(ann => /*#__PURE__*/React.createElement(Note, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id
  }))), /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-note")
  }, noteAnnotations.map(ann => /*#__PURE__*/React.createElement(Note, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id
  }))), /*#__PURE__*/React.createElement(Group, {
    className: "page-".concat(pageNumber, "-annotation-layer-info")
  }, infoAnnotations.map(ann => /*#__PURE__*/React.createElement(Info, {
    dpiScale: dpiScale,
    draggable: editMode && canEdit,
    offset: offset,
    pageHeight: pageHeight,
    scale: scale,
    selectedID: selectedID,
    onDragEnd: handleDragEnd,
    annotation: ann,
    key: ann.id
  }))), isDrawing ? lines.map((next, i) => {
    // Extract base type from composite key (e.g., 'line__lineAnnotation' -> 'line')
    const baseType = parsedSelectableType.baseType;
    // Use temporaryLineColorRef if available (from annotationHandler.create),
    // otherwise use defaultColor. This allows the temporary line to use the handler
    // color without altering global defaultColor
    // Using ref for immediate access without waiting for React re-render
    const colorToUse = temporaryLineColorRef.current || defaultColor;
    const tempColor = annTools.parseColor(colorToUse);
    return /*#__PURE__*/React.createElement(Line, {
      key: "temp-".concat(selectableType, "-").concat(next.id || i),
      name: "temp-".concat(selectableType),
      points: generatePointsArray(next, baseType === 'line'),
      stroke: "rgba(".concat(tempColor[0], ",").concat(tempColor[1], ",").concat(tempColor[2], ",0.70)"),
      lineCap: baseType === 'line' ? 'square' : 'round'
      // lines are always 8px wide scaled, inks are always same # of pxs regardless of scale
      ,
      strokeWidth: (baseType === 'line' ? lineThickness * (scale || 1) : baseInkThickness) / (dpiScale || 1),
      lineJoin: "round"
    });
  }) : null));
};
PDFAnnotationsLayer.propTypes = {
  annotation: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string
    }),
    groupedAnnotations: PropTypes.objectOf(PropTypes.objectOf(PropTypes.array))
  }).isRequired,
  annotationSelection: PropTypes.shape({
    annotationContext: PropTypes.string,
    selectedType: PropTypes.string,
    editMode: PropTypes.bool,
    selectableType: PropTypes.string,
    setClickedEntity: PropTypes.func,
    clickedEntity: PropTypes.shape({
      name: PropTypes.string,
      originalAnnotation: PropTypes.shape({
        rectString: PropTypes.string,
        inkAnnotation: PropTypes.shape({
          pathString: PropTypes.string // annotation tools parses the string into an array
        })
      })
    }),
    trackCursorPosition: PropTypes.bool,
    isDrawing: PropTypes.bool,
    defaultColor: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  canCreateDrawingAnnotations: PropTypes.func,
  canEditFormAnnotations: PropTypes.func,
  annotationSelectionActions: PropTypes.shape({
    batchChange: PropTypes.func,
    startDrawing: PropTypes.func,
    setClickedEntity: PropTypes.func,
    trackCursor: PropTypes.func,
    trackReset: PropTypes.func,
    setDefaultColor: PropTypes.func
  }).isRequired,
  pageNumber: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  hideTextLayer: PropTypes.func.isRequired,
  showTextLayer: PropTypes.func.isRequired,
  evaluateAnnotationHandlerColor: PropTypes.func,
  reportAnnotation: PropTypes.shape({
    active: PropTypes.shape({
      id: PropTypes.string,
      inkAnnotation: PropTypes.shape({
        pathString: PropTypes.string
      }),
      rectString: PropTypes.string
    }),
    freeTextsByPage: PropTypes.shape({}),
    highlightsByPage: PropTypes.shape({}),
    infosByPage: PropTypes.shape({}),
    inksByPage: PropTypes.shape({}),
    issuesByPage: PropTypes.shape({}),
    linesByPage: PropTypes.shape({}),
    trackCursorPosition: PropTypes.bool,
    loading: PropTypes.bool
  }).isRequired
};
export { PDFAnnotationsLayer as TestPDFAnnotationsLayer };
export default entityComposition(PDFAnnotationsLayer, ['annotation', 'annotationSelection', 'reportAnnotation']);